import CustomModal from 'components/Modal/CustomModal';
import React, { useState } from 'react';
import StorePayDetail from './StorePayDetail';
import { DEMO_HISTORY_STATUS, DEMO_RECORDING_TYPE } from 'utils/constants';
import { Icon, guestNoShow, success, backupSuccess, OffTopicIcon, TechDiffIcon } from 'assets/images';
import { calculateFacetimeDuration, calculateVideoDuration, convertNoShowQualificationTime, convertUTCToLocal } from 'utils/dateFormatter';
import PropTypes from 'prop-types';
import './viewListData.css';
import ImgComponent from 'components/imgComponent';
import { message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { HAS_DISCUSSED_BUSINESS } from 'utils/constants';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import axiosInstance from 'Services/axios';
import { StarFilled, StarOutlined } from "@ant-design/icons";
import logger from "utils/logger";
import useCan from 'hooks/useCan';
import { CAPABILITIES } from 'utils/capabilities';

/**
 * This function is used to get the demo summary
 *
 * @param {object} dataList dataList object
 * @return {object} demo summary object
 */

const getDemoSummary = (dataList) => {
  const { demo_analysis } = dataList;

  // Find the analysis entry with recording_type "MIXED"
  const mixedAnalysis = demo_analysis?.find(
    analysis => analysis?.recording_type === DEMO_RECORDING_TYPE.MIXED
  );

  const analysis = mixedAnalysis?.sentiment_analysis;

  // The backend blanks the summary and sentiment blocks a store's plan does not
  // include and flags them, so the lock state travels with the data rather than
  // being inferred from an absent value.
  return {
    overall_sentiment: analysis?.sentiment_analysis?.overall_meeting_sentiment,
    summary: analysis?.summary,
    summaryLocked: Boolean(analysis?.summary_locked),
    sentimentLocked: Boolean(analysis?.sentiment_locked),
  };
};

/**
 * This function is used to calculate the demo result status
 *
 * @param {string} guestAttended boolean value whether guest attended the meeting or not
 * @param {string} scheduledAt scheduled time of the demo
 * @param {string} startedAt start time of the demo
 * @param {string} endedAt end time of the demo
 * @return {ReactNode} element to display in the result status of the demo history table
 */

const getDemoResult = (
  guestAttended,
  scheduledAt,
  startedAt,
  endedAt,
  fallback_hosts_notified,
  status,
  absent_host
) => {
  if (status !== "ENDED" && absent_host) {
    return (
      <div>
        <img src={guestNoShow} alt="Real Customer No Show" />{' '}
        {DEMO_HISTORY_STATUS.HOST_NO_SHOW}
      </div>
    );
  }
  if (scheduledAt && endedAt) {
    let startedDate;
    const endedDate = new Date(endedAt);
    const scheduledDate = new Date(scheduledAt);
    
    if (!startedAt) {
      startedDate = new Date(scheduledAt);
    } else {
      startedDate = new Date(startedAt);
    }
    
    
    let timeDifference;

    if (startedDate < scheduledDate) {
      timeDifference = Math.abs(endedDate - scheduledDate);
    } else {
      timeDifference = Math.abs(endedDate - startedDate);
    }
    

    if (!guestAttended) {
      if (timeDifference > 600000) {
      

        return (
          <>
            <img src={guestNoShow} alt="Guest No Show" />{' '}
            {DEMO_HISTORY_STATUS.GUEST_NO_SHOW}
          </>
        );
      } else {
    

        return (
          <>
            <img src={guestNoShow} alt="Under Time" />{' '}
            {DEMO_HISTORY_STATUS.UNDER_TIME}
          </>
        );
      }
    }
    if (fallback_hosts_notified) {
    

      return (
        <>
          <img src={backupSuccess} alt="Back Up Success" />{' '}
          {DEMO_HISTORY_STATUS.BACKUP_SUCCESS}
        </>
      );
    }
    return (
      
      <>
        <img src={success} alt="Success" /> {DEMO_HISTORY_STATUS.SUCCESS}
      </>
    );
  }
  
  
};

/**
 * This component renders demo information data for a single demo in the demo history table.
 *
 * @param {props} props information regarding the demo
 * @return {ReactNode} This component renders demo information data for a single demo in the demo history table.
 */

const ViewListData = ({ dataList, hideHost = false }) => {
  const { allowed: canManageMeetings } = useCan(CAPABILITIES.MEETINGS_MANAGE);
  const [show, setShow] = useState(false);
  const [isBestOf, setIsBestOf] = useState(dataList?.is_best_of);
  const [showExtras, setShowExtras] = useState(false);
  const [showNoHost, setShowNoHost] = useState(false);
  const toggleBestOf = async (e) => {
    e.stopPropagation();

    try {
      const res = await axiosInstance.patch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/demo/${dataList.id}/best-of/`,
        {
          is_best_of: !isBestOf,
        }
      );

      setIsBestOf(res.data.is_best_of);

      messageApi.success(
        res.data.is_best_of
          ? "Marked as Best Meeting"
          : "Removed from Best Meetings"
      );
    } catch (err) {
      logger.error("Best Of toggle failed:", err);
      messageApi.error("Failed to update star");
    }
  };
  const [messageApi, contextHolder] = message.useMessage();
  const { duration: faceTimeDuration } = React.useMemo(() => {
    return calculateFacetimeDuration(
      dataList?.started_at,
      dataList?.ended_at, 
      dataList?.members[0]?.guest_detail?.attend_meeting,
      dataList?.members[0]?.guest_detail?.left_meeting,
      `${dataList?.schedule?.scheduled_date}T${dataList?.schedule?.scheduled_time}`
    );
  }, [
    dataList?.started_at,
    dataList?.ended_at,
    dataList?.members,
    dataList?.schedule?.scheduled_date,
    dataList?.schedule?.scheduled_time,
  ]);

  const primaryAttend = dataList?.members?.[0]?.guest_detail?.attend_meeting;

  const extraGuests = dataList?.members?.[0]?.extra_guests || [];

  const anyExtraJoined = extraGuests.some(
    (g) => g?.guest_detail?.attend_meeting
  );

  const guestAttended = primaryAttend || anyExtraJoined;

  const demoResult = dataList?.is_obscene_content ? (
    <>
      <FontAwesomeIcon icon={faBan} style={{ color: "#dc3545", marginRight: 4 }} />
      {DEMO_HISTORY_STATUS.OBSCENE_CONTENT}
    </>
  ) : (
  (dataList?.status === "UNDECIDED" && 
   dataList?.absent_host_details && 
   dataList?.fallback_hosts_notified && 
   !dataList?.started_at && 
   !dataList?.ended_at) ? (
    <>
      <img src={guestNoShow} alt="Host No Show" />{' '}
      {DEMO_HISTORY_STATUS.HOST_NO_SHOW}
    </>
  ) :dataList?.has_discussed_business_status !== HAS_DISCUSSED_BUSINESS.NA ? (
      dataList?.has_discussed_business_status === HAS_DISCUSSED_BUSINESS.NI ||
      dataList?.has_discussed_business_status === HAS_DISCUSSED_BUSINESS.IP ? (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "3px" }}
        >
          <FontAwesomeIcon
            icon={faHourglassHalf}
            style={{ color: "#FFD43B", paddingRight: 4, marginRight: 4 }}
          />
          {DEMO_HISTORY_STATUS.PROCESSING}
        </div>
      ) : dataList?.has_discussed_business_status ===
        HAS_DISCUSSED_BUSINESS.FIN ? (
        dataList?.has_discussed_business === true ? (
          <>
            {dataList?.fallback_hosts_notified ? (
              <>
                <img src={backupSuccess} alt='Back-Up Success' />{" "}
                {DEMO_HISTORY_STATUS.BACKUP_SUCCESS}
              </>
            ) : (
              <>
                <img src={success} alt='Success' />{" "}
                {DEMO_HISTORY_STATUS.SUCCESS}
              </>
            )}
          </>
        ) : dataList?.technical_difficulties ? (
          <>
            <img
              src={TechDiffIcon}
              alt='TECH DIFF'
              style={{ width: '20px', height: '20px' }}
            />{" "}
            {DEMO_HISTORY_STATUS.TECH_DIFF}
          </>
        ) :(
          <>
            <img src={OffTopicIcon} alt='OFF TOPIC' style={{width:"20px", height:"20px"}}/>{" "}
            {DEMO_HISTORY_STATUS.OFF_TOPIC}
          </>
        )
      ) : null
    ) : (
      getDemoResult(
        guestAttended,
        `${dataList?.schedule?.scheduled_date}T${dataList?.schedule?.scheduled_time}Z`,
        dataList?.started_at,
        dataList?.ended_at,
        dataList?.fallback_hosts_notified,
        dataList?.status,
        dataList?.absent_host_details
      )
    )
  );
  const handleClose = () => {
    setShow(false)
    setShowNoHost(false)
  };
  const handleShow = () => {
    if (dataList?.absent_host_details && !dataList?.started_at) {
      messageApi.open({
        type: "error",
        content:
          "Video meeting Details Not Available as the Real Customer did not attend the meeting. Click here to view the Available Details.",
        style:{cursor: 'pointer'},
        onClick: () => {
          setShow(true);
        }
      });
    } else {
      setShow(true);
    }
  };
  const handleShowHostNoShow = () => {
    messageApi.open({
      type: "error",
      content:
        "Video meeting Details Not Available as the Real Customer did not attend the meeting. Click here to view the Available Details.",
      style:{cursor: 'pointer'},
      onClick: () => {
        setShow(true);
        setShowNoHost(true);
      }
    });
  };
  const { started_at, ended_at, members, schedule } = dataList || {};
  const guestDetail = members?.[0]?.guest_detail;
  const demoSummaryData = getDemoSummary(dataList);
  return (
    <>
    {contextHolder}
      <tr onClick={handleShow}>
        <td>
          <div className="d-flex align-items-center">
            <div className="product-name">
              <span>
                {
                  convertUTCToLocal(
                    `${dataList?.schedule.scheduled_date}T${dataList?.schedule.scheduled_time}`
                  )[0]
                }
              </span>
            </div>
          </div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="">
              <ImgComponent
                className="host-pp-small"
                src={dataList?.product?.image ? dataList?.product.image : Icon}
                alt="product"
              />
            </div>{' '}
            <div className="product-name">
              <span>{dataList?.product?.name}</span>
            </div>
          </div>
        </td>
        <td>
          {demoResult}
        </td>
        <td>
            {dataList?.status === "ENDED"? calculateVideoDuration(
                started_at,
                ended_at,
                guestDetail?.attend_meeting,
                guestDetail?.left_meeting,
                `${schedule?.scheduled_date}T${schedule?.scheduled_time}`
              ): "NA"}
        </td>
        {!hideHost && (<td>
          <div className="d-flex align-items-center">
            <div className=""></div>{' '}
            <div className="">
              <span>
                {dataList?.absent_host_details
                  ? dataList?.absent_host_details?.host_detail?.first_name
                  : dataList?.members[0].host_detail?.first_name}{" "}
                {(dataList?.absent_host_details
                  ? dataList?.absent_host_details?.host_detail?.last_name
                  : dataList?.members[0].host_detail?.last_name
                )?.charAt(0)}
                .
              </span>
            </div>
          </div>
        </td>)}
        <td onClick={(e) => e.stopPropagation()}>
          {/* Primary guest */}
          {dataList?.members[0]?.guest_detail?.full_name || '-'}
          {/* Extra guests tooltip */}
          {dataList?.members[0]?.extra_guests?.length > 0 && (
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id={`tooltip-extra-guests`} className="extra-guest-tooltip">
                {dataList.members[0].extra_guests.map((g, i) => (
                  <div key={i}>{g?.guest_detail?.full_name}</div>
                ))}
              </Tooltip>
            }
          >
            <span
              style={{
                marginLeft: 6,
                color: "#0d6efd",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
              }}
              onClick={(e) => e.stopPropagation()} // prevent modal from opening
            >
              +{dataList.members[0].extra_guests.length}
            </span>
          </OverlayTrigger>
        )}
        </td>

      </tr>
      {(dataList?.absent_host_details && dataList?.status === "ENDED" ) && (
        <tr onClick={handleShowHostNoShow}>
          <td>
          <div className="d-flex align-items-center">
            <div className="product-name">
                <span>
                  {
                    convertUTCToLocal(
                      `${dataList?.schedule.scheduled_date}T${dataList?.schedule.scheduled_time}`
                    )[0]
                  }
                </span>
              </div>
            </div>
          </td>
          <td>
          <div className="d-flex align-items-center">
            <div className="">
                <ImgComponent
                className="host-pp-small"
                  src={dataList?.product.image ? dataList?.product.image : Icon}
                alt="product"
                />
            </div>{' '}
            <div className="product-name">
                <span>{dataList?.product.name}</span>
              </div>
            </div>
          </td>
          <td>
            <>
            <img src={guestNoShow} alt="Real Customer No Show" />{' '}
              {DEMO_HISTORY_STATUS.HOST_NO_SHOW}
            </>
          </td>
          <td>
            NA
          </td>
          <td>
          <div className="d-flex align-items-center">
            <div className=""></div>{' '}
            <div className="">
                <span>
                  {dataList?.absent_host_details?.host_detail?.first_name}{" "}
                  {dataList?.absent_host_details?.host_detail?.last_name.charAt(
                    0
                  )}
                  .
                </span>
              </div>
            </div>
          </td>
          <td>{dataList?.members[0].guest_detail.full_name}</td>
        </tr>
      )}
      <CustomModal
        showStatus={show}
        clickStatus={handleClose}
        buttonMessage='Close'
        closeStatus={handleClose}
        title={
          <div className='demo-details-modal-header'>
            <div>
              <h2>Meeting Details</h2>
              <div className='modal-demo-result'>
                Result: {'  '}
                {demoResult}
              </div>
            </div>
            {/* Best Of Star */}
            {canManageMeetings && (
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip
                    id={`tooltip-best-of-${dataList.id}`}
                    className="extra-guest-tooltip"
                  >
                    {isBestOf
                      ? "Remove from Best Meetings"
                      : "Mark this meeting as Best Of"}
                  </Tooltip>
                }
              >
                <div
                  onClick={toggleBestOf}
                  style={{
                    fontSize: "40px",
                    cursor: "pointer",
                    color: "rgb(255, 188, 11)",
                  }}
                >
                  {isBestOf ? <StarFilled /> : <StarOutlined />}
                </div>
              </OverlayTrigger>
            )}
          </div>
        }
        message={
          <StorePayDetail
            earning={dataList?.earning}
            guest={{
              primary: dataList?.members[0]?.guest_detail,
              extras: dataList?.members[0]?.extra_guests || []
            }}
            host={showNoHost ? dataList?.absent_host_details?.host_detail : dataList?.members[0].host_detail}
            product={dataList?.product}
            recording={dataList?.recorded_video}
            startedAt={dataList?.started_at}
            endedAt={dataList?.ended_at}
            soonestAt={dataList?.soonest_at}
            stars={dataList?.stars}
            review={dataList?.guest_review_note}
            schedule={dataList?.schedule}
            coupon_code={dataList?.coupon}
            summary={demoSummaryData?.summary}
            sentimentScore={demoSummaryData?.overall_sentiment}
            summaryLocked={demoSummaryData?.summaryLocked}
            sentimentLocked={demoSummaryData?.sentimentLocked}
            demo_analysis={dataList?.demo_analysis}
            meetingParticipants={
              dataList?.demo_analysis?.sentiment_analysis?.meeting_participants
            }
            hasDiscussedBusinessStatus={dataList?.has_discussed_business_status}
            faceTimeDuration={faceTimeDuration}
            demoId={dataList?.id}
            technicalUsers={dataList?.technical_difficulties_users}
          />
        }
        secondMessage='noClose'
        secondButton={false}
        modalClass='demo-details-modal'
      />
    </>
  );
};

ViewListData.propTypes = {
  dataList: PropTypes.shape({
    schedule: PropTypes.object,
    product: PropTypes.object,
    members: PropTypes.array,
    started_at: PropTypes.string,
    ended_at: PropTypes.string,
    soonest_at: PropTypes.string,
    earning: PropTypes.number,
    recorded_video: PropTypes.string,
    stars: PropTypes.number,
    guest_review_note: PropTypes.string,
    coupon_code: PropTypes.string,
  }).isRequired,
};

export default ViewListData;